// src/theme/tokens.ts
// -----------------------------------------------------------------------------
// Sistema de design "Aurora" — fonte única de verdade para cores, gradientes,
// sombras e tipografia. Consumido tanto pelo tema MUI quanto pelos
// styled-components (via CSS variables).
// -----------------------------------------------------------------------------

export type Mode = 'light' | 'dark';

/* Acentos vibrantes compartilhados entre os dois modos (tema azul) */
export const accent = {
    indigo: '#2b7bff',
    violet: '#3b82f6',
    cyan: '#22d3ee',
    rose: '#fb7185',
    amber: '#fbbf24',
    emerald: '#34d399',
};

export const gradients = {
    /* gradiente principal de marca */
    brand: 'linear-gradient(135deg, #1e63ff 0%, #3b82f6 55%, #22d3ee 100%)',
    brandSoft: 'linear-gradient(135deg, #2b7bff 0%, #3b82f6 100%)',
    cyan: 'linear-gradient(135deg, #2b7bff 0%, #22d3ee 100%)',
    /* Destaque quente. Existe justamente para NÃO ser azul: é o que sobrevive
       em cima da pílula azul do item selecionado. */
    highlight: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
    /* fundo da sidebar (sempre escuro e sofisticado nos dois modos) */
    sidebarLight: 'linear-gradient(185deg, #1d2236 0%, #141826 60%, #0f1320 100%)',
    sidebarDark: 'linear-gradient(185deg, #141828 0%, #0c0f1a 60%, #080a12 100%)',
};

interface Palette {
    bg: string;
    bgGradient: string;
    surface: string;
    surface2: string;
    surfaceGlass: string;
    border: string;
    borderStrong: string;
    text: string;
    textMuted: string;
    textDim: string;
    shadow: string;
    shadowLg: string;
    glow: string;
    scrollThumb: string;
    /* sidebar */
    sidebarBg: string;
    sidebarText: string;
    sidebarTextMuted: string;
    sidebarHover: string;
    sidebarBorder: string;
}

export const palettes: Record<Mode, Palette> = {
    light: {
        bg: '#eef0f8',
        bgGradient:
            'radial-gradient(1200px 600px at 100% -10%, rgba(43,123,255,0.10), transparent 60%), radial-gradient(900px 500px at -10% 110%, rgba(34,211,238,0.10), transparent 55%), #eef0f8',
        surface: '#ffffff',
        surface2: '#f7f8fd',
        surfaceGlass: 'rgba(255,255,255,0.72)',
        border: '#e6e8f4',
        borderStrong: '#d6d9ec',
        text: '#191c2e',
        textMuted: '#5b6079',
        textDim: '#8a8fab',
        shadow: '0 1px 2px rgba(20,22,46,0.04), 0 6px 20px rgba(20,22,46,0.06)',
        shadowLg: '0 8px 30px rgba(31,32,80,0.12)',
        glow: '0 8px 24px rgba(43,123,255,0.30)',
        scrollThumb: 'rgba(20,22,46,0.18)',
        sidebarBg: gradients.sidebarLight,
        sidebarText: 'rgba(232,235,250,0.78)',
        sidebarTextMuted: 'rgba(232,235,250,0.45)',
        sidebarHover: 'rgba(255,255,255,0.06)',
        sidebarBorder: 'rgba(255,255,255,0.08)',
    },
    dark: {
        bg: '#0b0e16',
        bgGradient:
            'radial-gradient(1200px 600px at 100% -10%, rgba(43,123,255,0.16), transparent 60%), radial-gradient(900px 500px at -10% 110%, rgba(34,211,238,0.12), transparent 55%), #0b0e16',
        surface: '#151826',
        surface2: '#1b1f30',
        surfaceGlass: 'rgba(21,24,38,0.72)',
        border: '#262b3f',
        borderStrong: '#333a52',
        text: '#eef0fa',
        textMuted: '#a4a9c6',
        textDim: '#6c7290',
        shadow: '0 1px 2px rgba(0,0,0,0.4), 0 8px 24px rgba(0,0,0,0.35)',
        shadowLg: '0 12px 40px rgba(0,0,0,0.55)',
        glow: '0 8px 28px rgba(43,123,255,0.45)',
        scrollThumb: 'rgba(255,255,255,0.14)',
        sidebarBg: gradients.sidebarDark,
        sidebarText: 'rgba(228,231,247,0.80)',
        sidebarTextMuted: 'rgba(228,231,247,0.42)',
        sidebarHover: 'rgba(255,255,255,0.05)',
        sidebarBorder: 'rgba(255,255,255,0.06)',
    },
};

export const fonts = {
    display: `'Outfit', 'Plus Jakarta Sans', system-ui, sans-serif`,
    body: `'Plus Jakarta Sans', 'Outfit', system-ui, sans-serif`,
};

/* Gera o bloco de CSS variables para um modo, consumido pelos styled-components */
export const cssVars = (mode: Mode): Record<string, string> => {
    const p = palettes[mode];
    return {
        '--bg': p.bg,
        '--bg-gradient': p.bgGradient,
        '--surface': p.surface,
        '--surface-2': p.surface2,
        '--surface-glass': p.surfaceGlass,
        '--border': p.border,
        '--border-strong': p.borderStrong,
        '--text': p.text,
        '--text-muted': p.textMuted,
        '--text-dim': p.textDim,
        '--shadow': p.shadow,
        '--shadow-lg': p.shadowLg,
        '--glow': p.glow,
        '--scroll-thumb': p.scrollThumb,
        '--accent': accent.indigo,
        '--accent-2': accent.violet,
        '--accent-3': accent.cyan,
        '--accent-rose': accent.rose,
        '--accent-amber': accent.amber,
        '--gradient-brand': gradients.brand,
        '--gradient-brand-soft': gradients.brandSoft,
        '--gradient-cyan': gradients.cyan,
        '--gradient-highlight': gradients.highlight,
        '--sidebar-bg': p.sidebarBg,
        '--sidebar-text': p.sidebarText,
        '--sidebar-text-muted': p.sidebarTextMuted,
        '--sidebar-hover': p.sidebarHover,
        '--sidebar-border': p.sidebarBorder,
        '--font-display': fonts.display,
        '--font-body': fonts.body,
    };
};
